/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { processImage } from 'js/lib/imgix';

import { Button, breakpoints } from '@coursera/cds-core';
import { SvgPlayFilled } from '@coursera/coursera-ui/svg';
import { useTracker } from '@coursera/event-pulse/react';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import type { PremiumHubImageAssetFragment } from 'bundles/premium-hub/__generated__/contentfulTypes';
import Overlay from 'bundles/xddp/components/shared/Overlay';

import _t from 'i18n!nls/premium-hub';

type Props = {
  videoEmbedUrl?: string | null;
  placeholderImage: PremiumHubImageAssetFragment;
  index: number;
  authorName?: string | null;
  authorTitle?: string | null;
  text?: string | null;
  slideNumber: number;
};

const styles = {
  videoPlaceholderContainer: (imageUrl: string) => css`
    cursor: pointer;
    ${breakpoints.up('sm')} {
      height: 434px;
    }
    ${breakpoints.down('sm')} {
      height: 200px;
    }

    position: relative;
    z-index: 3;
    width: 100%;
    border-radius: 8px;
    background-image: url(${imageUrl});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  `,
  playIcon: css`
    position: absolute;
    z-index: 3;
    top: calc(50% - var(--cds-spacing-300));
    left: calc(50% - var(--cds-spacing-300));
    border: 3px solid var(--cds-color-white-0);
    border-radius: calc(var(--cds-border-radius-400) + var(--cds-border-radius-50));
    width: 66px;
    height: 66px;
    padding: calc(var(--cds-spacing-150) / 2 + var(--cds-spacing-100));
    margin: 0 auto;
    background-color: var(--cds-color-darken-400);

    :hover,
    :active,
    :focus {
      background-color: var(--cds-color-darken-400);
    }
  `,
  blackOverlay: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 2;
    background-color: var(--cds-color-neutral-primary);
    opacity: 0.5;
  `,

  // Default dimensions to render Youtube video properly
  videoPlayerContainer: css`
    @media (min-width: 288px) {
      height: 128px;
    }

    @media (min-width: 576px) {
      height: 324px;
    }

    @media (min-width: 792px) {
      height: 445px;
    }

    @media (min-width: 1008px) {
      height: 567px;
    }

    @media (min-width: 1152px) {
      height: 648px;
    }
  `,
};

const TestimonialVideo = ({
  videoEmbedUrl,
  placeholderImage,
  index,
  authorName,
  authorTitle,
  text,
  slideNumber,
}: Props) => {
  const [showVideoPlayback, setVideoPlayback] = useState(false);
  const trackV3 = useTracker();

  if (!videoEmbedUrl || !placeholderImage) {
    return null;
  }

  const compressedImageUrl = processImage(placeholderImage.url, {
    auto: 'format,compress',
    width: 550,
    quality: 60,
  });

  return (
    <TrackedDiv
      trackingName="testimonial"
      data={{ indexPosition: index + 1, testimonialName: authorName ?? '', testimonialTitle: authorTitle ?? '' }}
      withVisibilityTracking
      onClick={() => {
        setVideoPlayback(!showVideoPlayback);
        trackV3('click_testimonial_card', {
          testimonialCard: {
            author: authorName ?? '',
            text: text ?? '',
            index: index ?? -1,
          },
          testimonialAction: 'play_video',
        });
      }}
      role="button"
      aria-label={_t('Slide #{slideNumber}. #{description}', {
        slideNumber,
        description: placeholderImage.description,
      })}
    >
      <div css={styles.videoPlaceholderContainer(compressedImageUrl)}>
        <Button variant="ghost" css={styles.playIcon} tabIndex={-1}>
          <SvgPlayFilled size={36} color="white" />
        </Button>
        <div css={styles.blackOverlay} />
      </div>

      {showVideoPlayback && (
        <div data-testid="open-video-overlay">
          <Overlay handleClose={() => setVideoPlayback(!showVideoPlayback)} isOpen={showVideoPlayback}>
            <div css={styles.videoPlayerContainer}>
              <iframe
                title={_t('Youtube Embedded Video')}
                width="100%"
                height="100%"
                src={videoEmbedUrl}
                frameBorder="0"
                allowFullScreen
              />
            </div>
          </Overlay>
        </div>
      )}
    </TrackedDiv>
  );
};

export default TestimonialVideo;
